import React from 'react';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Table from 'core/components/VirtualTable';
import {
  useSelectableContext,
} from 'core/hooks';
import {
  Row,
} from '@tanstack/react-table';
import clsx from 'clsx';
import {
  getApiBillingGetDeliveredPatientStatementsPatientId,
} from 'endpoints';
import TableRow from 'core/components/VirtualTable/Row';
import {
  useGetDeliveredPatientStatements,
  useGetPatientStatementEncounterSnapshots,
} from 'pages/Dashboard/pages/Ledger/hooks';
import {
  DeliveredPatientStatementDTO,
} from 'dtos/deliveredPatientStatementDTO';
import SelectableProvider from 'core/context/selectable';
import Skeleton from 'core/components/Skeleton';
import columns from 'pages/Dashboard/pages/Ledger/statementColumns';
import {
  formatDate,
} from 'utils/date';
import Tag from 'core/components/Tag';
import {
  formatMoney,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import dayjs from 'utils/dayjs';

type EncountersProps = {
  row: DeliveredPatientStatementDTO;
};

const borderClasses = 'border-t border-[#dde2ee]';

function Encounters({ row }: EncountersProps) {
  const {
    data = [],
    isLoading,
  } = useGetPatientStatementEncounterSnapshots(row.patientStatementDeliveryLogId ?? 0);
  return (isLoading
    ? (
      <div className="flex flex-col">
        <div className="flex items-center px-2 py-4 gap-4 border-t border-t-[#dde2ee] first:border-0">
          <Skeleton className="w-full !h-4" />
        </div>
        <div className="flex items-center px-2 py-4 gap-4 border-t border-t-[#dde2ee] first:border-0">
          <Skeleton className="w-full !h-4" />
        </div>
      </div>
    ) : (
      <div className={clsx('overflow-x-auto text-xs w-full', { [borderClasses]: data.length > 0 })}>
        {data.map((item) => {
          const isPaid = (item?.remainingDue ?? 0) <= 0;
          return (
            <div
              key={item?.encounterId}
              className="flex py-2 gap-4 border-t border-t-[#dde2ee] first:border-t-0 justify-between"
            >
              <div className="flex items-center flex-1 gap-1 px-2 font-semibold">
                {formatDate(item?.startDateTime ?? '')}
                <Tag tagName={item?.reasonForVisit ?? ''} className="w-fit max-w-xs truncate" />
              </div>
              <div className="flex gap-4 justify-between w-[130px]">
                <div className="flex flex-col gap-px">
                  <span className="font-semibold">
                    {formatMoney(item?.due ?? 0)}
                  </span>
                  <span>
                    {dayjs(item?.dueAccruedOn).to(row?.sentOn)}
                  </span>
                </div>
                <ToolTip title={isPaid ? 'Paid' : `Due: ${formatMoney(item?.remainingDue ?? 0)}`}>
                  <span className="cursor-pointer w-6">
                    {isPaid
                      ? <CheckCircleIcon className="!text-green-600 !w-4 !h-4" />
                      : <CancelOutlinedIcon className="!text-red-600 !w-4 !h-4" />}
                  </span>
                </ToolTip>
              </div>
            </div>
          );
        })}
      </div>
    )
  );
}

type EncounterHistoryRowProps = {
  row: Row<DeliveredPatientStatementDTO>;
  hasSkeleton: boolean;
};

function StatementHistoryRow({ row, hasSkeleton }: EncounterHistoryRowProps) {
  const { isSelected } = useSelectableContext();
  const { patientStatementDeliveryLogId } = row?.original ?? {};
  const expanded = isSelected(patientStatementDeliveryLogId ?? '');
  return (
    <>
      <TableRow<DeliveredPatientStatementDTO>
        row={row}
        cellClassName={clsx('min-h-[40px] !items-center')}
        hasSkeleton={hasSkeleton}
      />
      {expanded && (<Encounters row={row.original} />)}
    </>
  );
}

type Params = {
  patientId: number;
  key: string;
};

export type StatementHistoryPanelInfo = { patientId: number };

export default function StatementHistory({
  onClose,
  entity: { patientId },
}: SidePanelComponentProps<StatementHistoryPanelInfo>) {
  const { data = {}, isLoading } = useGetDeliveredPatientStatements(patientId);
  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      <div className="flex flex-col p-4">
        <div className="flex justify-between">
          <h1 className="text-xl font-semibold">Statement History</h1>
          <IconButton onClick={onClose}>
            <CloseIcon className="!text-xl" />
          </IconButton>
        </div>
        <div className="flex flex-col gap-2 font-semibold">
          <p className="text-gray-400 text-sm">Statements received</p>
          <p className="text-gray-900 text-xl">
            {isLoading
              ? <Skeleton className="w-20 !h-4 my-1.5" />
              : data?.count}
          </p>
        </div>
      </div>
      <div className="w-full h-full flex flex-col overflow-hidden">
        <div className="flex-1 w-full h-full">
          <SelectableProvider>
            <Table<DeliveredPatientStatementDTO>
              columns={columns}
              queryParams={{
                patientId,
                // @TODO: implement proper data provider, so it won't needed to do this
                key: 'delivered-patient-statements',
              }}
              getData={async ({ patientId }: Params) => {
                const response = await getApiBillingGetDeliveredPatientStatementsPatientId(
                  patientId,
                );
                return response?.data ?? [];
              }}
              emptySlot={<div className="text-center">No statements found.</div>}
              renderRowComponent={(row) => (
                <StatementHistoryRow
                  row={row}
                  hasSkeleton={isLoading}
                />
              )}
              skeleton={{ rows: 3 }}
            />
          </SelectableProvider>
        </div>
      </div>
    </div>
  );
}
